import React, { useState } from "react";
import { Alert, Form, Input } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getErrorProps, makeRequest } from "@utils/helpers";
import { ButtonComponent } from "@comps/components";
import { notifySuccess } from "@comps/commonFunctions";
import { reset } from "../requests";
import { Icon } from "@iconify/react";
import AuthLayout from "./AuthLayout";
import "./auth.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const linkIsValid = Boolean(token && email);

  const clearErrors = () => {
    setErrors([]);
    setErrorMsg("");
  };

  const onFinish = (values) => {
    clearErrors();

    const payload = {
      email,
      token,
      password: values.password,
      password_confirmation: values.password_confirmation,
    };
    makeRequest(setLoader, reset, payload, onResetSuccess, onResetError);
  };

  const onResetSuccess = (data, res) => {
    notifySuccess(res?.msg || "Password changed successfully. Please log in.");
    navigate('/signin');
  };

  // An expired or already-used link comes back as a 412; a 422 carries the
  // validation messages in data. Both stay on screen until the next submit.
  const onResetError = (data, res) => {
    const detail = Array.isArray(data) && data.length ? data[0] : null;
    const msg = detail || res?.msg || "Unable to reset your password. Please request a new link.";
    setErrorMsg(msg);
    setErrors({ password: msg });
  };

  const rules = {
    password: [
      { required: true, message: 'Please input your new password!', },
      { min: 6, message: 'Minimum password length is 6', },
      { max: 32, message: 'Maximum password length is 32', },
    ],
    password_confirmation: [
      { required: true, message: 'Please confirm your new password!', },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value || getFieldValue('password') === value) {
            return Promise.resolve();
          }
          return Promise.reject(new Error('The two passwords do not match.'));
        },
      }),
    ],
  };

  const backToLogin = (
    <Link className="auth-link auth-back" to="/signin">
      <Icon icon="tabler:arrow-left" width={ 16 } />
      Back to login
    </Link>
  );

  // A link that lost its token or email can not be completed — say so and
  // point at the one action that helps instead of showing a dead form.
  if (!linkIsValid) {
    return (
      <AuthLayout
        title="This link is not valid"
        subtitle="The reset link is missing information. Open the link straight from your email, or request a new one."
        footer={ backToLogin }
      >
        <div className="auth-icon-badge">
          <Icon icon="tabler:link-off" width={ 28 } />
        </div>

        <ButtonComponent
          className="actionButton"
          type="primary"
          onClick={ () => navigate('/forgot-password') }
        >
          Request a new link
        </ButtonComponent>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle={ <>Choose a new password for <span className="auth-email">{ email }</span>.</> }
      footer={ backToLogin }
    >
      { errorMsg &&
        <Alert
          className="auth-alert"
          type="error"
          showIcon
          message={ errorMsg }
        />
      }

      <Form
        className="auth-form"
        layout="vertical"
        requiredMark={ false }
        name="resetPasswordForm"
        onFinish={ onFinish }
      >
        <Form.Item
          name="password"
          onChange={ clearErrors }
          rules={ rules.password }
          label="New password"
          { ...getErrorProps(errors['password']) }
        >
          <Input.Password
            placeholder="Enter your new password"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="password_confirmation"
          dependencies={ ['password'] }
          rules={ rules.password_confirmation }
          label="Confirm new password"
        >
          <Input.Password
            placeholder="Re-enter your new password"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item className="da-mb-0">
          <ButtonComponent className="actionButton" type="primary" htmlType="submit" state={ loader }>
            Reset password
          </ButtonComponent>
        </Form.Item>
      </Form>
    </AuthLayout>
  );
};

export default ResetPassword;
